import React, { useState, useEffect, useContext } from 'react';
import { Amplify, Auth, Storage, Hub } from 'aws-amplify';
import { HubCallback, Credentials } from '@aws-amplify/core';

import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import Calendar from 'dayjs/plugin/calendar';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { MabelUser, MabelUserRole, UserContext } from './contexts/UserContext';

import { useSessionStorageState, useCountDown } from 'ahooks';

import {
  BrowserRouter as Router,
  Link,
  Route,
  Switch,
  Redirect,
  RouteProps,
  useLocation,
} from 'react-router-dom';
import {
  Layout,
  Avatar,
  notification,
  Result,
  Modal,
  Button,
  Menu,
  Dropdown,
} from 'antd';
import {
  BarChartOutlined,
  FormOutlined,
  HomeOutlined,
  LogoutOutlined,
  SettingOutlined,
  UploadOutlined,
  UserOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';

import { ApolloProvider, useApolloClient } from '@apollo/react-hooks';
import ApolloClient from 'apollo-client';
import { createHttpLink } from 'apollo-link-http';
import { setContext } from 'apollo-link-context';
import { onError } from 'apollo-link-error';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { cacheIdFromObject } from './graphql/utils';

import {
  useGetUserDetailsQuery,
  useUpsertUserMutation,
  UserBasicFragment,
  Users_Update_Column,
} from './graphql/generated';

import './App.less';
import LoginPage from './pages/LoginPage';
import TemplatesAdminPage from './pages/TemplatesAdminPage';
import CampaignsAdminPage from './pages/CampaignsAdminPage';
import NetworksAdminPage from './pages/NetworksAdminPage';
import PracticesAdminPage from './pages/PracticesAdminPage';
import DataChangeRequestsPage from './pages/DataChangeRequestsPage';
import ProvidersAdminPage from './pages/ProvidersAdminPage';
import InsuranceSegmentMappingPage from './pages/InsuranceSegmentMappingPage';
import SettingsPage from './pages/SettingsPage';
import ClientOrganizationProfilePage from './pages/ClientOrganizationProfilePage';
import ClientPracticesPage from './pages/ClientPracticesPage';
import ClientTemplatesPage from './pages/ClientTemplatesPage';

import PracticeHomePage from './pages/PracticeHomePage';
import OnboardingPage from './pages/OnboardingPage';
import PatientsPage from './pages/PatientsPage';
import ProvidersPage from './pages/ProvidersPage';
import AlignmentProcessingPage from './pages/AlignmentProcessingPage';
import HEDRProcessingPage from './pages/HEDRProcessingPage';
import UnknownFormsPage from './pages/UnknownFormsPage';
import PatientsAdminPage from './pages/PatientsAdminPage';
import CampaignsPage from './pages/CampaignsPage';
import CampaignManagerPage from './pages/CampaignManagerPage';
import PracticeAnalyticsPage from './pages/PracticeAnalyticsPage';
import ClientHomePage from './pages/ClientHomePage';
import OrganizationHomePage from './pages/OrganizationHomePage';
import ClientFileUploadsPage from './pages/ClientFileUploadsPage';
import ClientSVAReviewPage from './pages/ClientSVAReviewPage';
import OrganizationsAdminPage from './pages/OrganizationsAdminPage';

import UsersAdminPage from './pages/UsersAdminPage';
import MabelNavBar from './components/MabelNavBar';
import { BuildingOutlined, ClinicOutlined } from './components/MabelIcons';
import useWindowDimensions from './hooks/useWindowDimensions';
import usePageTracking from './hooks/usePageTracking';
import { ReactComponent as MabelLogo } from './images/Mabel_Logo_White_RGB.svg';
import MabelFavicon from './images/Mabel_Favicon_White_Transparent_260.png';

import { SentryLink } from 'apollo-link-sentry';
import * as Sentry from '@sentry/browser';
import { HelmetProvider } from 'react-helmet-async';
import { useIdleTimer } from 'react-idle-timer';

import PdfSplitterPage from './pages/PdfSplitterPage';

// Capture OAuth errors from URL before Amplify or other libraries consume them
const params = new URLSearchParams(window.location.search);
const error = params.get('error');
const error_description = params.get('error_description');
if (error || error_description) {
  sessionStorage.setItem(
    'auth_error',
    JSON.stringify({ error, error_description })
  );
}

if (process.env.NODE_ENV !== 'development') {
  Sentry.init({
    dsn:
      'https://0447a7f374604b6bbc41400ea1392911@o261006.ingest.sentry.io/5280645',
    environment: process.env.REACT_APP_SENTRY_ENV,
    // apparently the ResizeObserver error is benign:
    // https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications.',
    ],
  });
}

const aws_config = {
  aws_project_region: process.env.REACT_APP_AWS_REGION,
  aws_cognito_identity_pool_id: process.env.REACT_APP_COGNITO_IDENTITY_POOL_ID,
  aws_cognito_region: process.env.REACT_APP_AWS_REGION,
  aws_user_pools_id: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  aws_user_pools_web_client_id: process.env.REACT_APP_COGNITO_CLIENT_ID,
  aws_user_files_s3_bucket: process.env.REACT_APP_PRIVATE_STORAGE_BUCKET,
  aws_user_files_s3_bucket_region: process.env.REACT_APP_AWS_REGION,
  oauth: {
    domain: process.env.REACT_APP_COGNITO_AUTH_DOMAIN,
    scope: ['profile', 'email', 'openid'],
    redirectSignIn: `${process.env.REACT_APP_DOMAIN}/login`,
    redirectSignOut: process.env.REACT_APP_DOMAIN,
    responseType: 'code',
  },
};
Amplify.configure(aws_config);
Auth.configure(aws_config);
// Storage level should either be 'private' or 'protected'.
// To minimize risk of a breach, we use private by default.
Storage.configure({ level: 'private' });

dayjs.extend(localizedFormat);
dayjs.extend(Calendar);
dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);

function App() {
  const [user, setUser] = useState<MabelUser | undefined>();
  const [userRole, setUserRole] = useSessionStorageState<
    MabelUserRole | undefined
  >('userRole', undefined);

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then((cognitoUser) => setUser(new MabelUser(cognitoUser, userRole)))
      .catch((err) => {});

    const onAuthEvent: HubCallback = (data) => {
      if (
        data.payload.event === 'signIn' ||
        data.payload.event === 'tokenRefresh' ||
        data.payload.event === 'verify'
      ) {
        Auth.currentAuthenticatedUser()
          .then((cognitoUser) => setUser(new MabelUser(cognitoUser, userRole)))
          .catch((err) => {});
      } else if (data.payload.event === 'signIn_failure') {
        // if data.payload.data?.message starts with PreTokenGeneration
        // and message contains one of the following:
        //  SSO_NOT_ALLOWED_FOR_PASSWORD_USER
        //  PASSWORD_NOT_ALLOWED_FOR_GOOGLE_USER
        //  PASSWORD_NOT_ALLOWED_FOR_OKTA_USER
        // then it's a special kind of error resulting in a mismatch between user and login type
        // In this case, we'll display a special message with a link to the
        // generic hosted UI (which is necessary because the google "account chooser"
        // is not available otherwise)
        if (
          data.payload.data?.message &&
          /SSO_NOT_ALLOWED_FOR_PASSWORD_USER|PASSWORD_NOT_ALLOWED_FOR_GOOGLE_USER|PASSWORD_NOT_ALLOWED_FOR_OKTA_USER/.test(
            data.payload.data.message
          )
        ) {
          notification.error({
            message: 'Login Failed',
            description: (
              <>
                Incorrect user or login type
                <p>
                  <Button
                    type="link"
                    onClick={() => {
                      Auth.federatedSignIn();
                    }}
                    style={{ padding: 0 }}
                  >
                    Try again here
                  </Button>
                </p>
              </>
            ),
            duration: 0,
          });
          setUser(undefined);
        }
      } else {
        setUser(undefined);
      }
    };
    Hub.listen('auth', onAuthEvent);
    return () => Hub.remove('auth', onAuthEvent);
  }, [userRole]);

  const setRole = (role: MabelUserRole) => {
    if (user) {
      setUser(new MabelUser(user.cognito_user, role));
      setUserRole(role);
    }
  };
  return (
    <UserContext.Provider value={{ user: user, setRole: setRole }}>
      <AppHome />
    </UserContext.Provider>
  );
}

function AppHome() {
  const { user } = useContext(UserContext);
  const httpLink = createHttpLink({
    uri: process.env.REACT_APP_HASURA_URL,
  });
  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.map((graphQLError) => {
        const event_id = Sentry.captureMessage(graphQLError.message);
        Sentry.showReportDialog({
          eventId: event_id,
          user: {
            name: user?.claims?.name || undefined,
            email: user?.claims?.email || undefined,
          },
          labelComments:
            'What happened? (Please do not include PHI your description)',
        });
        notification.error({
          message: 'GraphQL Error',
          description: graphQLError.message,
          placement: 'bottomLeft',
          duration: 6,
        });
        return graphQLError;
      });
    }

    if (networkError) {
      Sentry.captureException(networkError);
      notification.error({
        message: 'Network Error',
        description: networkError.message,
        placement: 'bottomLeft',
        duration: 6,
      });
    }
  });
  const authLink = setContext(async (_, { headers }) => {
    const newHeaders = { ...headers };
    newHeaders['x-hasura-role'] = user?.role || 'public';
    if (user?.jwt) {
      // Calling Auth.currentSession() forces a token refresh if the current one expired
      const token = (await Auth.currentSession()).getIdToken().getJwtToken();
      if (token) newHeaders.authorization = `Bearer ${token}`;
    }
    return { headers: newHeaders };
  });
  const sentryLink = new SentryLink({
    breadcrumb: {
      includeQuery: true,
      includeError: true,
    },
  });

  const client = new ApolloClient({
    link: authLink.concat(errorLink).concat(sentryLink).concat(httpLink),
    connectToDevTools: process.env.NODE_ENV === 'development',
    cache: new InMemoryCache({
      dataIdFromObject: cacheIdFromObject,
    }),
  });

  return (
    <HelmetProvider>
      <ApolloProvider client={client}>
        <Router>
          <AppWithRoutes />
        </Router>
      </ApolloProvider>
    </HelmetProvider>
  );
}

const AppWithRoutes = () => {
  usePageTracking();
  const { user } = useContext(UserContext);
  return (
    <Switch>
      <Route path="/login">
        <LoginPage />
      </Route>
      <Route path="/pdf-splitter">
        <PdfSplitterPage />
      </Route>
      <PrivateRoute path="/" user={user}>
        <ProtectedAppHome />
      </PrivateRoute>
    </Switch>
  );
};

// A wrapper for <Route> that redirects to the login
// screen if you're not yet authenticated.
interface PrivateRouteProps extends RouteProps {
  user: MabelUser | undefined;
}
const PrivateRoute: React.FC<PrivateRouteProps> = ({
  user,
  children,
  ...rest
}) => {
  return (
    <Route
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
      render={({ location }) =>
        user ? (
          children
        ) : (
          <Redirect
            to={{
              pathname: '/login',
              state: { from: location },
            }}
          />
        )
      }
    />
  );
};

function ProtectedAppHome() {
  const { user } = useContext(UserContext);
  const [, setUserRole] = useSessionStorageState('userRole', undefined);

  const client = useApolloClient();
  const signOut = () =>
    Auth.signOut().then(() => {
      setUserRole(undefined);
      client.clearStore();
    });

  const [, setLogoutTime] = useCountDown({
    onEnd: () => {
      Modal.destroyAll();
      signOut();
    },
  });

  useIdleTimer({
    timeout: 1000 * 60 * 15,
    crossTab: {
      emitOnAllTabs: true,
      channelName: 'mabel-idle-timer',
      type: 'localStorage',
    },
    onIdle: () => {
      setLogoutTime(Date.now() + 60 * 1000);
      Modal.confirm({
        title: 'Your session is expiring',
        content: 'You will be logged out due to inactivity soon',
        okText: 'Stay Logged In',
        cancelText: 'Logout',
        onCancel: signOut,
        onOk: () => setLogoutTime(undefined),
      });
    },
    debounce: 500,
  });

  //Tell Sentry the user, for logging purposes
  Sentry.configureScope(function (scope) {
    scope.setUser({
      id: user?.user_id,
      email: user?.claims?.email || undefined,
      role: user?.role || undefined,
    });
  });

  // Cognito is the source of truth for user info.
  // Every time user logs in, we upsert their info to the DB
  // to ensure we're in sync
  const [upsertUser] = useUpsertUserMutation();
  useEffect(() => {
    (async () => {
      const credentials = await Credentials.get();
      const user_immutable: Partial<UserBasicFragment> = {
        user_id: user?.user_id,
        groups: user?.allowed_roles,
        identity_id: credentials.identityId || null,
        last_login_at: null /* forces automatic update via db trigger */,
      };
      upsertUser({
        variables: {
          user: {
            user_name: user?.claims?.name || '',
            user_email: user?.claims?.email,
            ...user_immutable,
          },
          cols_to_update: Object.keys(user_immutable) as Users_Update_Column[],
        },
      });
    })();
  }, [user, upsertUser]);

  const { height } = useWindowDimensions();

  if (user?.role === 'client_user') {
    return (
      <ClientUserLayout height={height}>
        <Switch>
          <Route exact path={['/client-home']}>
            <ClientHomePage />
          </Route>
          <Route path="/dashboards">
            <OrganizationHomePage />
          </Route>
          <Route exact path="/sva-reviews">
            <ClientSVAReviewPage />
          </Route>
          <Route exact path="/file-uploads">
            <ClientFileUploadsPage />
          </Route>
          <Route exact path="/aco-entities">
            <ClientOrganizationProfilePage />
          </Route>
          <Route exact path="/client-practices">
            <ClientPracticesPage />
          </Route>
          <Route exact path="/template-previews">
            <ClientTemplatesPage />
          </Route>
          <Route exact path={['/settings', '/settings/:entity']}>
            <SettingsPage />
          </Route>
          <Redirect to="/client-home" />
        </Switch>
      </ClientUserLayout>
    );
  }

  return (
    <Layout>
      <MabelNavBar />
      <Layout.Content
        style={{
          padding: '24px 24px 0px',
          marginTop: 64,
          minHeight: height - 134, //header = 64, footer = 70
        }}
      >
        <div
          className="layout-page-background"
          style={{ padding: 24, minHeight: height - 134 - 48 }}
        >
          {user?.role === 'admin' ? (
            <Switch>
              <Route path="/templates">
                <TemplatesAdminPage />
              </Route>
              <Route path="/campaigns">
                <CampaignsAdminPage />
              </Route>
              <Route path="/campaign-manager/:campaign_template_id">
                <CampaignManagerPage />
              </Route>
              <Route path="/practices">
                <PracticesAdminPage />
              </Route>
              <Route path="/data-change-requests">
                <DataChangeRequestsPage />
              </Route>
              <Route path="/organizations">
                <OrganizationsAdminPage />
              </Route>
              <Route path="/networks">
                <NetworksAdminPage />
              </Route>
              <Route path="/users">
                <UsersAdminPage />
              </Route>
              <Route path="/providers">
                <ProvidersAdminPage />
              </Route>
              <Route path="/patients">
                <PatientsAdminPage />
              </Route>
              <Route path="/svas">
                <AlignmentProcessingPage />
              </Route>
              <Route path="/health-equity-reports">
                <HEDRProcessingPage />
              </Route>
              <Route path="/unknown-forms">
                <UnknownFormsPage />
              </Route>
              <Route path="/insurance-mapping">
                <InsuranceSegmentMappingPage />
              </Route>
              <Route exact path={['/settings', '/settings/:entity']}>
                <SettingsPage />
              </Route>
              <Redirect to="/practices" />
            </Switch>
          ) : user?.role === 'network_user' ? (
            <Switch>
              <Route path="/providers">
                <ProvidersPage />
              </Route>
              <Route exact path={['/settings', '/settings/:entity']}>
                <SettingsPage />
              </Route>
              <Redirect to="/providers" />
            </Switch>
          ) : user?.role === 'organization_user' ? (
            <Switch>
              <Route path="/dashboards">
                <OrganizationHomePage />
              </Route>
              <Route exact path={['/settings', '/settings/:entity']}>
                <SettingsPage />
              </Route>
              <Redirect to="/dashboards" />
            </Switch>
          ) : user?.role === 'practice_user' ? (
            <Switch>
              <Route exact path={['/']}>
                <PracticeHomePage />
              </Route>
              <Route exact path={['/onboarding', '/onboarding/:step']}>
                <OnboardingPage />
              </Route>
              <Route path="/patients">
                <PatientsPage />
              </Route>
              <Route path="/campaigns">
                <CampaignsPage />
              </Route>
              <Route path="/analytics">
                <PracticeAnalyticsPage />
              </Route>
              <Route exact path={['/settings', '/settings/:entity']}>
                <SettingsPage />
              </Route>
              <Redirect to="/" />
            </Switch>
          ) : (
            <Result title="User doesn't have a role assigned" status="error" />
          )}
        </div>
      </Layout.Content>
      <Layout.Footer style={{ textAlign: 'center' }}>
        {process.env.REACT_APP_WEBSITE_NAME} ©{new Date().getFullYear()}
      </Layout.Footer>
    </Layout>
  );
}

const ClientUserLayout: React.FC<{
  children: React.ReactNode;
  height: number;
}> = ({ children, height }) => {
  const [collapsed, setCollapsed] = useState(false);
  const { user, setRole } = useContext(UserContext);
  const [, setUserRole] = useSessionStorageState('userRole', undefined);
  const client = useApolloClient();
  const location = useLocation();
  const userEmail = user?.claims?.email || '';
  const { data: userDetails } = useGetUserDetailsQuery({
    variables: { user_id: user?.claims?.sub },
  });
  const userAvatarSource =
    userDetails?.users_by_pk?.photo ||
    user?.claims?.picture ||
    user?.claims?.photo;
  const userInitials = (user?.claims?.name || userEmail || '?')
    .split(' ')
    .map((name: string) => name[0])
    .join('')
    .toUpperCase();
  const selectedKey = location.pathname.startsWith('/dashboards')
    ? 'dashboards'
    : location.pathname.startsWith('/sva-reviews')
    ? 'sva-reviews'
    : location.pathname.startsWith('/file-uploads')
    ? 'file-uploads'
    : location.pathname.startsWith('/aco-entities')
    ? 'aco-entities'
    : location.pathname.startsWith('/client-practices')
    ? 'practices'
    : location.pathname.startsWith('/template-previews')
    ? 'forms'
    : location.pathname.startsWith('/settings')
    ? 'settings-profile'
    : 'client-home';
  const canSwitchRoles = (user?.allowed_roles || []).length > 1;
  const roleSwitcherMenu = (
    <Menu selectedKeys={[user?.role || '']}>
      {[...(user?.allowed_roles || [])].sort().map((role) => (
        <Menu.Item
          key={role}
          disabled={role === user?.role}
          onClick={(event) => {
            setRole(event.key as MabelUserRole);
            client.clearStore();
          }}
        >
          {role}
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Layout.Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        breakpoint="lg"
      >
        <div
          className={`client-layout-logo ${
            collapsed ? 'client-layout-logo-collapsed' : ''
          }`}
        >
          <Link to="/client-home">
            {collapsed ? <img src={MabelFavicon} alt="Mabel" /> : <MabelLogo />}
          </Link>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          defaultOpenKeys={
            location.pathname.startsWith('/settings') ? ['settings'] : []
          }
          className="client-layout-menu"
        >
          <Menu.Item key="client-home" icon={<HomeOutlined />}>
            <Link to="/client-home">Home</Link>
          </Menu.Item>
          <Menu.Item key="file-uploads" icon={<UploadOutlined />}>
            <Link to="/file-uploads">File Uploads</Link>
          </Menu.Item>
          <Menu.Item key="practices" icon={<ClinicOutlined />}>
            <Link to="/client-practices">Practices</Link>
          </Menu.Item>
          <Menu.Item key="aco-entities" icon={<BuildingOutlined />}>
            <Link to="/aco-entities">ACO Entities</Link>
          </Menu.Item>
          <Menu.Item key="forms" icon={<FormOutlined />}>
            <Link to="/template-previews">Forms</Link>
          </Menu.Item>
          <Menu.Item key="dashboards" icon={<BarChartOutlined />}>
            <Link to="/dashboards">Dashboards</Link>
          </Menu.Item>
          <Menu.SubMenu key="settings" icon={<UserOutlined />} title="Profile">
            <Menu.ItemGroup
              key="profile-email"
              title={
                <span
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                    fontSize: 12,
                    gap: 8,
                    lineHeight: 1.5,
                    marginLeft: -8,
                    overflow: 'hidden',
                  }}
                >
                  <Avatar
                    src={userAvatarSource}
                    size={18}
                    style={{
                      backgroundColor: '#006b77',
                      color: '#fff',
                      flex: '0 0 auto',
                    }}
                  >
                    {userInitials}
                  </Avatar>
                  <span
                    style={{
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {userEmail || 'Signed in'}
                  </span>
                </span>
              }
            />
            <Menu.Item key="settings-profile" icon={<SettingOutlined />}>
              <Link to="/settings">Profile Settings</Link>
            </Menu.Item>
            <Menu.Item
              key="logout"
              icon={<LogoutOutlined />}
              onClick={() => {
                Auth.signOut().then(() => {
                  setUserRole(undefined);
                  client.clearStore();
                });
              }}
            >
              Logout
            </Menu.Item>
          </Menu.SubMenu>
        </Menu>
      </Layout.Sider>
      <Layout>
        <Layout.Content
          style={{
            padding: '24px 24px 0px',
            minHeight: height - 70,
          }}
        >
          <div
            className="layout-page-background"
            style={{ padding: 24, minHeight: height - 70 - 48 }}
          >
            {children}
          </div>
        </Layout.Content>
        <Layout.Footer style={{ textAlign: 'center' }}>
          {process.env.REACT_APP_WEBSITE_NAME} ©{new Date().getFullYear()}
        </Layout.Footer>
      </Layout>
      {canSwitchRoles ? (
        <Dropdown
          overlay={roleSwitcherMenu}
          placement="topRight"
          trigger={['click']}
        >
          <Button
            className="client-role-switcher"
            type="primary"
            shape="round"
            size="large"
            icon={<UserSwitchOutlined />}
          >
            Switch Role
          </Button>
        </Dropdown>
      ) : null}
    </Layout>
  );
};

export default App;
